import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import { LanguageProvider, detectPreferredLang } from './contexts/LanguageContext';
import { ROUTES, LANGUAGES, DEFAULT_LANG, localizedPath } from './seo/routes';
import { FormProvider } from './contexts/FormContext';
import { AuthProvider } from './contexts/AuthContext';
import { useAuth } from './contexts/AuthContext';
import type { Capability } from './config/capabilities';
import type { FeatureKey } from './config/features';
import './i18n';
import './App.css';

// Public pages
import HomePage from './pages/HomePage';
import EmergencyPage from './pages/EmergencyPage';
import EmergencyBrokenToothPage from './pages/EmergencyBrokenToothPage';
import EmergencyToothachePage from './pages/EmergencyToothachePage';
import EmergencyTouristsPage from './pages/EmergencyTouristsPage';
import EmergencyAbscessPage from './pages/EmergencyAbscessPage';
import ContactPage from './pages/ContactPage';
import PrivacyPage from './pages/PrivacyPage';
import LegalNoticePage from './pages/LegalNoticePage';
import ImplantsPage from './pages/ImplantsPage';
import VeneersPage from './pages/VeneersPage';
import CompositePage from './pages/CompositePage';
import TechnologiesPage from './pages/TechnologiesPage';
import EmergencyButton from './components/EmergencyButton';
import SmartAIPanel from './components/smartai/SmartAIPanel';
import LanguageTest from './components/LanguageTest';
import CookieConsent from './components/CookieConsent';
import { BANNER_HEIGHT_VAR } from './components/ClosureBanner';
import ScrollToTop from './components/ScrollToTop';

// Lazy geladene Bereiche — eigene Chunks, damit die öffentliche Website sie
// nicht mitlädt. Der Admin-Bereich (Kalender, Rechnungen, Patienten) und die
// per Mail-Link erreichbaren Token-Seiten werden von Besuchern der
// Startseite nie geöffnet, hingen aber im selben Bundle.
const IntakePage = lazy(() => import('./pages/IntakePage'));
const ManageAppointmentPage = lazy(() => import('./pages/ManageAppointmentPage'));
const RecallUnsubscribePage = lazy(() => import('./pages/RecallUnsubscribePage'));
const LoginPage = lazy(() => import('./components/admin/LoginPage'));
const MagicLinkVerifyPage = lazy(() => import('./components/admin/MagicLinkVerifyPage'));
const AdminLayout = lazy(() => import('./components/admin/AdminLayout'));
const Dashboard = lazy(() => import('./components/admin/Dashboard'));
const CalendarView = lazy(() => import('./components/admin/CalendarView'));
const ProvidersPage = lazy(() => import('./components/admin/ProvidersPage'));
const ProceduresPage = lazy(() => import('./components/admin/ProceduresPage'));
const TemplatesAdminPage = lazy(() => import('./components/admin/TemplatesAdminPage'));
const AppointmentsPage = lazy(() => import('./components/admin/AppointmentsPage'));
const QuickAppointmentPage = lazy(() => import('./components/admin/QuickAppointmentPage'));
const IntakeListPage = lazy(() => import('./components/admin/IntakeListPage'));
const PatientsListPage = lazy(() => import('./components/admin/PatientsListPage'));
const PatientDetailPage = lazy(() => import('./components/admin/PatientDetailPage'));
const InvoiceListPage = lazy(() => import('./components/admin/billing/InvoiceListPage'));
const InvoiceEditorPage = lazy(() => import('./components/admin/billing/InvoiceEditorPage'));
const CatalogPage = lazy(() => import('./components/admin/billing/CatalogPage'));
const OperatoriesPage = lazy(() => import('./components/admin/OperatoriesPage'));
const HelpPage = lazy(() => import('./components/admin/HelpPage'));
const SettingsPage = lazy(() => import('./components/admin/SettingsPage'));
const EventLogPage = lazy(() => import('./components/admin/EventLogPage'));
const InboxPage = lazy(() => import('./components/admin/crm/InboxPage'));
const KnowledgeBasePage = lazy(() => import('./components/admin/smartai/KnowledgeBasePage'));
const ReportsPage = lazy(() => import('./components/admin/ReportsPage'));
const RemindersPage = lazy(() => import('./components/admin/RemindersPage'));
const RecallsPage = lazy(() => import('./components/admin/RecallsPage'));

// Protected route component for admin
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-navy-admin"></div>
      </div>
    );
  }
  
  return isAuthenticated ? <>{children}</> : <Navigate to="/admin/login" replace />;
};

// Bereichswächter: blockt den direkten Aufruf eines Bereichs ohne Berechtigung
// (Defense-in-Depth zusätzlich zum ausgeblendeten Menüpunkt). Fehlt das Recht,
// geht es zurück aufs Dashboard, das jeder Benutzer sehen darf.
// `feature` prüft zusätzlich die Vendor-Feature-Flags (Paket-Steuerung) —
// nicht gebuchte Bereiche sind auch per Direkt-URL nicht erreichbar.
const RequirePermission: React.FC<{ capability: Capability; feature?: FeatureKey; children: React.ReactNode }> = ({ capability, feature, children }) => {
  const { can, hasFeature } = useAuth();
  if (feature && !hasFeature(feature)) return <Navigate to="/admin" replace />;
  return can(capability) ? <>{children}</> : <Navigate to="/admin" replace />;
};

// Nur-Admin-Wächter für sensible Konfigurationsbereiche (Einstellungen, Stammdaten).
const AdminOnlyRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  return user?.role === 'admin' ? <>{children}</> : <Navigate to="/admin" replace />;
};

// Map von Routen-Schlüssel (siehe src/seo/routes.json) auf die Seitenkomponente.
const PUBLIC_COMPONENTS: Record<string, React.FC> = {
  home: HomePage,
  implants: ImplantsPage,
  composite: CompositePage,
  veneers: VeneersPage,
  technologies: TechnologiesPage,
  emergency: EmergencyPage,
  'emergency-broken-tooth': EmergencyBrokenToothPage,
  'emergency-toothache': EmergencyToothachePage,
  'emergency-tourists': EmergencyTouristsPage,
  'emergency-abscess': EmergencyAbscessPage,
  contact: ContactPage,
  privacy: PrivacyPage,
  legal: LegalNoticePage,
};

// Redirect, der Query-String und Hash des aktuellen Pfads erhält (für Legacy-URLs).
const Redirect: React.FC<{ to: string }> = ({ to }) => {
  const { search, hash } = useLocation();
  return <Navigate to={`${to}${search}${hash}`} replace />;
};

// Einstieg ohne Sprach-Präfix ("/") → Weiterleitung auf die erkannte Sprache.
const RootRedirect: React.FC = () => {
  const { search, hash } = useLocation();
  return <Navigate to={`${localizedPath('home', detectPreferredLang())}${search}${hash}`} replace />;
};

// Ladeanzeige für die per React.lazy nachgeladenen Bereiche. Bewusst
// bildschirmfüllend und ohne Layoutsprung — sie ersetzt nie bereits
// sichtbaren Inhalt, weil nur Admin- und Token-Routen lazy sind.
const RouteFallback: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-cream">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy" />
  </div>
);

function App() {
  return (
    <AuthProvider>
      <Router>
        <ScrollToTop />
        <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* Patient intake questionnaire — token-based public access, own focused
              layout (no Header/Footer, own translations). Reached via email link. */}
          <Route path="/intake/:token" element={<IntakePage />} />
          <Route path="/termin/:token" element={<ManageAppointmentPage />} />
          <Route path="/recall/abmelden/:token" element={<RecallUnsubscribePage />} />

          {/* Admin Routes - separate layout (outside LanguageProvider) */}
          <Route path="/admin/login" element={<LoginPage />} />
          <Route path="/admin/login/verify" element={<MagicLinkVerifyPage />} />
          <Route path="/admin" element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          }>
            <Route index element={<Dashboard />} />
            <Route path="calendar" element={<RequirePermission capability="calendar" feature="calendar"><CalendarView /></RequirePermission>} />
            <Route path="quick-appointment" element={<RequirePermission capability="appointments" feature="appointments"><QuickAppointmentPage /></RequirePermission>} />
            <Route path="appointments" element={<RequirePermission capability="appointments" feature="appointments"><AppointmentsPage /></RequirePermission>} />
            <Route path="reminders" element={<RequirePermission capability="reminders" feature="reminders"><RemindersPage /></RequirePermission>} />
            <Route path="recalls" element={<RequirePermission capability="recalls" feature="recalls"><RecallsPage /></RequirePermission>} />
            <Route path="intake" element={<RequirePermission capability="intake" feature="intake"><IntakeListPage /></RequirePermission>} />
            <Route path="patients" element={<RequirePermission capability="patients" feature="patients"><PatientsListPage /></RequirePermission>} />
            <Route path="patients/:id" element={<RequirePermission capability="patients" feature="patients"><PatientDetailPage /></RequirePermission>} />
            <Route path="invoices" element={<RequirePermission capability="invoices" feature="invoices"><InvoiceListPage /></RequirePermission>} />
            <Route path="invoices/catalog" element={<RequirePermission capability="invoices-manage" feature="invoices"><CatalogPage /></RequirePermission>} />
            <Route path="invoices/new" element={<RequirePermission capability="invoices-manage" feature="invoices"><InvoiceEditorPage /></RequirePermission>} />
            <Route path="invoices/:id" element={<RequirePermission capability="invoices" feature="invoices"><InvoiceEditorPage /></RequirePermission>} />
            <Route path="providers" element={<AdminOnlyRoute><ProvidersPage /></AdminOnlyRoute>} />
            <Route path="procedures" element={<AdminOnlyRoute><ProceduresPage /></AdminOnlyRoute>} />
            <Route path="note-templates" element={<AdminOnlyRoute><TemplatesAdminPage /></AdminOnlyRoute>} />
            <Route path="operatories" element={<AdminOnlyRoute><OperatoriesPage /></AdminOnlyRoute>} />
            <Route path="reports" element={<RequirePermission capability="reports" feature="reports"><ReportsPage /></RequirePermission>} />
            <Route path="inbox" element={<RequirePermission capability="communications" feature="inbox"><InboxPage /></RequirePermission>} />
            <Route path="smartai" element={<AdminOnlyRoute><KnowledgeBasePage /></AdminOnlyRoute>} />
            <Route path="event-log" element={<RequirePermission capability="event-log" feature="event-log"><EventLogPage /></RequirePermission>} />
            <Route path="settings" element={<AdminOnlyRoute><SettingsPage /></AdminOnlyRoute>} />
            <Route path="help" element={<HelpPage />} />
          </Route>

          {/* Public Website Routes - original layout (with LanguageProvider) */}
          <Route path="*" element={
            <LanguageProvider>
              <FormProvider>
                <div className="flex flex-col min-h-screen">
                  <Header />
                  {/* Der Header ist fixed und überlagert die Seite; jede Seite
                      hält dafür oben Platz frei. Zeigt der Header zusätzlich das
                      Urlaubsband, wächst er — um genau dessen Höhe rückt der
                      Inhalt nach (Variable setzt ClosureBanner). */}
                  <div className="flex-grow" style={{ paddingTop: `var(${BANNER_HEIGHT_VAR}, 0px)` }}>
                    <Routes>
                      {/* Einstieg ohne Sprach-Präfix → erkannte Sprache */}
                      <Route path="/" element={<RootRedirect />} />

                      {/* Lokalisierte Routen: /<lang>/<slug> pro Seite × Sprache */}
                      {ROUTES.flatMap((route) =>
                        LANGUAGES.map((lang) => {
                          const Component = PUBLIC_COMPONENTS[route.key];
                          const slug = route.slugs[lang];
                          const path = slug ? `/${lang}/${slug}` : `/${lang}`;
                          return <Route key={`${route.key}-${lang}`} path={path} element={<Component />} />;
                        })
                      )}

                      <Route path="/test" element={<LanguageTest />} />

                      {/* Legacy-URLs (vor der Sprach-Umstellung) → präfixierte Version.
                          Deutsche Slugs → /de, ehemalige englische Aliase → /en. */}
                      <Route path="/implantate" element={<Redirect to="/de/implantate" />} />
                      <Route path="/veneers" element={<Redirect to="/de/veneers" />} />
                      <Route path="/composite" element={<Redirect to="/de/composite" />} />
                      <Route path="/technologien" element={<Redirect to="/de/technologien" />} />
                      <Route path="/kontakt" element={<Redirect to="/de/kontakt" />} />
                      <Route path="/datenschutz" element={<Redirect to="/de/datenschutz" />} />
                      <Route path="/impressum" element={<Redirect to="/de/impressum" />} />
                      <Route path="/emergency" element={<Redirect to="/de/notdienst" />} />
                      <Route path="/implants" element={<Redirect to="/en/implants" />} />
                      <Route path="/contact" element={<Redirect to="/en/contact" />} />
                      <Route path="/technologies" element={<Redirect to="/en/technologies" />} />
                      <Route path="/legal-notice" element={<Redirect to="/en/legal-notice" />} />
                      <Route path="/privacy" element={<Redirect to="/en/privacy" />} />
                      <Route path="/book" element={<Navigate to="/de/kontakt?booking=true" replace />} />
                      <Route path="/booking" element={<Navigate to="/de/kontakt?booking=true" replace />} />

                      {/* Unbekannte öffentliche Pfade → Default-Sprache */}
                      <Route path="*" element={<Navigate to={`/${DEFAULT_LANG}`} replace />} />
                    </Routes>
                  </div>
                  <Footer />
                  <EmergencyButton />
                  <SmartAIPanel />
                  <CookieConsent />
                </div>
              </FormProvider>
            </LanguageProvider>
          } />
        </Routes>
        </Suspense>
      </Router>
    </AuthProvider>
  );
}

export default App;
